// LSP-19866: browser extensions on users' machines throw inside our page and the Datadog Logs
// (forwardErrorsToLogs) and RUM SDKs report them as Contact Admin errors. Over the last 30 days
// in production the top such signature alone was the #1 error in the whole app (16,663 events)
// and carried no stack we could act on. The datadog.ts beforeSend hooks call this helper on
// error-shaped events only and drop the event when it matches; non-error RUM events (view,
// action, resource) and non-error-status logs are never passed through it.
//
// Every signature below is anchored to a phrase an application error cannot plausibly produce.
// Add a new entry only with the extension/source and the observed Datadog volume in its comment.
const BROWSER_EXTENSION_MESSAGE_SIGNATURES: readonly RegExp[] = [
	// Microsoft Editor / Edge extension content script — "Object Not Found Matching Id:12,
	// MethodName:update, ParamCount:4" (also MethodName:simulateEvent). 16,663 events / ~37 sessions.
	/Object Not Found Matching Id:\d+, MethodName:/,
	// Unknown ad/coupon extension reading its own `M_ID` config off an undefined global —
	// "Cannot read properties of undefined (reading 'M_ID')". 1,749 events / 8 sessions.
	/reading 'M_ID'/,
	// Chrome extension messaging API — "Invalid call to runtime.sendMessage(). Tab not found."
	// Only extension code can call chrome.runtime.sendMessage. 131 events.
	/runtime\.sendMessage\(\)/,
	// MetaMask wallet extension inpage provider — "Failed to connect to MetaMask". 102 events.
	/MetaMask/
];

// These three schemes are the only extension origins in supported browsers.
const BROWSER_EXTENSION_STACK_ORIGIN = /\b(?:chrome-extension|moz-extension|safari-web-extension):\/\//;

// AI review (LSP-19866): a SINGLE extension frame is not proof the failure is the extension's.
// Extensions monkey-patch fetch/XHR/DOM APIs, so their wrapper frame lands on the stack of a
// genuine Contact Admin failure — dropping on any extension frame would silence real errors.
// A stack only counts as extension-origin when every frame that carries a URL is an extension
// URL, i.e. no application http(s) frame took part in the throw at all.
function stackHasOnlyExtensionFrames(stack: string): boolean {
	const urlFrames = stack.split("\n").filter((line) => line.includes("://"));
	if (urlFrames.length === 0) {
		return false;
	}
	return urlFrames.every((frame) => BROWSER_EXTENSION_STACK_ORIGIN.test(frame));
}

export function isBrowserExtensionNoise(message: string | undefined, stack?: string | undefined): boolean {
	if (message && BROWSER_EXTENSION_MESSAGE_SIGNATURES.some((signature) => signature.test(message))) {
		return true;
	}
	return Boolean(stack && stackHasOnlyExtensionFrames(stack));
}
